import { type FormEvent, useMemo, useState } from "react";

import { useSession } from "../context/SessionContext";
import { useToast } from "../context/ToastContext";

const COMMON_TIMEZONES = [
  "UTC",
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Ho_Chi_Minh",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Perth",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function browserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
}

export function TimezonePreference({ compact = false }: { compact?: boolean }) {
  const { kind, user, updateTimezone } = useSession();
  const { showToast } = useToast();
  const fallback = kind === "staff" ? "Asia/Ho_Chi_Minh" : "UTC";
  const [timezone, setTimezone] = useState(user?.timezone || fallback);
  const [busy, setBusy] = useState(false);
  const options = useMemo(() => Array.from(new Set([
    user?.timezone || fallback,
    browserTimezone(),
    ...COMMON_TIMEZONES,
  ])).sort(), [fallback, user?.timezone]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await updateTimezone(timezone);
      showToast("Timezone saved. Dates and times have been updated.", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Timezone could not be saved.", "error");
    } finally { setBusy(false); }
  }

  return (
    <form className={`timezone-preference${compact ? " timezone-preference--compact" : ""}`} onSubmit={save}>
      <label className="compact-select">
        <span className={compact ? "sr-only" : undefined}>Timezone</span>
        <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
          {options.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
        </select>
      </label>
      <button className="button button--secondary button--small" type="submit" disabled={busy || timezone === user?.timezone}>
        {busy ? "Saving…" : "Save"}
      </button>
    </form>
  );
}
